import React, { useEffect, useState } from 'react';
import { BiEdit, BiPlus, BiTrash } from 'react-icons/bi';

import Bouton from '../../UI/Bouton/Bouton';
import ChampDeTexte from '../../UI/ChampDeTexte/ChampDeTexte';
import Tableau from '../../UI/tableau/Tableau';

import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import S from './TableauTasks.module.scss';

const TableauTasks = ({
  taskSelectionnee,
  editerTache,
  creerTache,
  supprimerTache,
  tasks,
}) => {
  const [filtreNom, setFiltreNom] = useState('');
  const [filtreId, setFiltreId] = useState('');
  const [tachesFiltrees, setTachesFiltrees] = useState([]);

  useEffect(() => {
    let filtered = tasks;
    if (filtreNom) {
      filtered = filtered.filter((t) =>
        (t.nom || '').toLowerCase().includes(filtreNom.toLowerCase())
      );
    }
    if (filtreId) {
      filtered = filtered.filter((t) =>
        (t.id || '').toString().includes(filtreId)
      );
    }
    setTachesFiltrees(filtered);
  }, [tasks, filtreNom, filtreId]);

  const handleEdit = (e, tache) => {
    e.stopPropagation();
    if (typeof editerTache === 'function') editerTache(tache);
  };

  const handleDelete = (e, tache) => {
    e.stopPropagation();
    if (typeof supprimerTache === 'function') {
      supprimerTache(tache);
    }
  };

  const selectRow = {
    mode: 'radio',
    clickToSelect: true,
    hideSelectAll: true,
    hideSelectColumn: true,
    style: () => ({ background: '#eeb214', color: 'black' }),
    onSelect: (row) => {
      if (typeof taskSelectionnee === 'function') return taskSelectionnee(row);
    },
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDayName = (dayNum) => {
    const days = {
      0: 'Dim',
      1: 'Lun',
      2: 'Mar',
      3: 'Mer',
      4: 'Jeu',
      5: 'Ven',
      6: 'Sam',
    };
    return days[dayNum] || dayNum;
  };

  const getFrequencyDisplay = (row) => {
    if (row.type === 'manual') {
      return 'Manuel';
    }

    if (row.type === 'planned') {
      // Pour planned: afficher la date de lancement (CRON_start_day)
      return row.cron_start_day ? formatDate(row.cron_start_day) : '-';
    }

    if (row.type === 'scheduled') {
      // Vérifier si c'est une fréquence basée sur les jours de la semaine
      if (row.cron_day_of_week && row.cron_day_of_week !== '*') {
        const days = row.cron_day_of_week
          .split(',')
          .map((d) => getDayName(d.trim()));
        const time =
          row.cron_hour !== '*' && row.cron_minute !== '*'
            ? ` à ${row.cron_hour}:${row.cron_minute.padStart(2, '0')}`
            : '';
        return `${days.join(', ')}${time}`;
      }

      // Pour scheduled: construire l'affichage de la fréquence (intervalle de répétition)
      const parts = [];

      const year = parseInt(row.cron_year) || 0;
      const month = parseInt(row.cron_month) || 0;
      const week = parseInt(row.cron_week) || 0;
      const day = parseInt(row.cron_day) || 0;
      const hour = parseInt(row.cron_hour) || 0;
      const minute = parseInt(row.cron_minute) || 0;
      const second = parseInt(row.cron_second) || 0;

      if (year > 0) parts.push(`${year} an${year > 1 ? 's' : ''}`);
      if (month > 0) parts.push(`${month} mois`);
      if (week > 0) parts.push(`${week} semaine${week > 1 ? 's' : ''}`);
      if (day > 0) parts.push(`${day} jour${day > 1 ? 's' : ''}`);
      if (hour > 0) parts.push(`${hour}h`);
      if (minute > 0) parts.push(`${minute}min`);
      if (second > 0) parts.push(`${second}s`);

      return parts.length > 0 ? `Tous les ${parts.join(' ')}` : 'Planifié';
    }

    return '-';
  };

  const colonnes = [
    { dataField: 'task_id', text: 'ID', sort: true, style: { width: '10%' } },
    { dataField: 'name', text: 'Tâche', sort: true, style: { width: '12%' } },
    {
      dataField: 'function_name',
      text: 'Fonction',
      sort: true,
      style: { width: '12%' },
    },
    { dataField: 'type', text: 'Mode', sort: true, style: { width: '10%' } },
    {
      dataField: 'state',
      text: 'Active',
      sort: true,
      style: { width: '8%' },
    },
    {
      dataField: 'frequency',
      text: 'Fréquence/Date',
      isDummyField: true,
      sort: false,
      style: { width: '14%' },
      formatter: (cell, row) => <span>{getFrequencyDisplay(row)}</span>,
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      style: { width: '14%' },
      formatter: (cell, row) => (
        <div className={S.actionsContainer}>
          <Bouton
            type={type_btn.secondary}
            rond={true}
            taille="small"
            cliquer={(e) => handleEdit(e, row)}
            ajoutClass={`${S.actionButton} ${S.editButton}`}
          >
            <BiEdit size={14} />
          </Bouton>
          <Bouton
            type={type_btn.danger}
            rond={true}
            taille="small"
            cliquer={(e) => handleDelete(e, row)}
            ajoutClass={S.actionButton}
          >
            <BiTrash size={14} />
          </Bouton>
        </div>
      ),
    },
  ];

  return (
    <div className={S.conteneurTableau}>
      <div className={S.filtresContainer}>
        <Bouton
          type={type_btn.success}
          cliquer={creerTache}
          ajoutClass={S.boutonCreer}
        >
          <BiPlus size={16} /> Nouvelle tâche
        </Bouton>
      </div>

      <div className={S.tableauContainer}>
        <Tableau
          cleLigne="task_id"
          donnees={tachesFiltrees}
          colonnes={colonnes}
          selectionnerLignes={selectRow}
          messageVide="Aucune tâche trouvée"
          ajoutClass={S.tableau}
          hide_general_search={false}
        />
      </div>

      <div className={S.infoContainer}>
        <span className={S.infoText}>
          Total tâches: {tachesFiltrees.length}
        </span>
      </div>
    </div>
  );
};

export default TableauTasks;
